import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import { Flex, Header, Loader } from 'nallian-shared-ui/lib/components';
import communityTextsService from '../../services/communityTexts/communityTextService';
import createCommunityTextService from '../../services/communityTexts/createCommunityTextService';
import updateCommunityTextService from '../../services/communityTexts/updateCommunityTextService';
import CommunityTextForm from './CommunityTextForm';
import apiRoutes from '../../../../app/api/routes';

const name = 'Welcome text';

const mapFormToCommunityText = form => ({
  id: form?.id,
  type: form?.type,
  name,
  contents: form?.communityText?.en,
  languageOverrides: Object.keys(form?.communityText)
    .map(k => ({ languageId: k, contents: form?.communityText[k], name }))
    .filter(l => l && l.contents),
});

const CommunityTexts = ({
  dispatchCommunityTextsGet,
  communityTextsFetch,
  communityTag,
  languagesFetch,
  dispatchCreateCommunityTextPost,
  dispatchUpdateCommunityTextPut,
  createCommunityTextFetch,
  updateCommunityTextFetch,
  type,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (communityTag) dispatchCommunityTextsGet(communityTag);
  }, [dispatchCommunityTextsGet, communityTag]);

  const languageOptions = useMemo(() => {
    const languages = [
      ...(languagesFetch.value || []).map(l => ({ ...l, active: Boolean(l.id === 'en') })),
    ];

    (communityTextsFetch.value || []).forEach(b =>
      (b.languageOverrides || []).forEach(lo =>
        languages.push({ id: lo.languageId, active: false })
      )
    );

    return uniqBy(languages, 'id');
  }, [languagesFetch.value, communityTextsFetch.value]);

  const initialValues = useMemo(() => {
    const text = (communityTextsFetch.value || []).find(
      txt => txt.type?.toLowerCase() === type.toLowerCase()
    );
    const value = {
      id: text?.id,
      communityText: {
        en: text?.contents,
      },
      type,
    };

    // eslint-disable-next-line no-unused-expressions
    text?.languageOverrides?.forEach(l => {
      value.communityText[l.languageId] = l.contents;
    });

    return value;
  }, [communityTextsFetch.value, type]);

  const handleSubmit = useCallback(
    values => {
      const callback = () => dispatchCommunityTextsGet(communityTag);
      if (values.id)
        dispatchUpdateCommunityTextPut(
          communityTag,
          values.id,
          mapFormToCommunityText(values),
          callback,
          t
        );
      else
        dispatchCreateCommunityTextPost(communityTag, mapFormToCommunityText(values), callback, t);
    },
    [
      communityTag,
      dispatchCommunityTextsGet,
      dispatchCreateCommunityTextPost,
      dispatchUpdateCommunityTextPut,
      t,
    ]
  );

  return (
    <Flex fluid direction="column">
      <Header style={{ marginTop: '0' }} dividing>
        {t(`${type}Text`)}
      </Header>
      <Flex margin="0 0 4rem 0">
        {!languagesFetch.pending && !communityTextsFetch.pending ? (
          <CommunityTextForm
            key={type}
            type={type}
            initialValues={initialValues}
            languageOptions={languageOptions}
            onSubmit={handleSubmit}
            submitting={createCommunityTextFetch.pending || updateCommunityTextFetch.pending}
          />
        ) : (
          <Loader inline active />
        )}
      </Flex>
    </Flex>
  );
};

CommunityTexts.propTypes = {
  dispatchCommunityTextsGet: PropTypes.func.isRequired,
  communityTextsFetch: PropTypes.object.isRequired,
  communityTag: PropTypes.string.isRequired,
  defaultLanguageId: PropTypes.string.isRequired,
  languagesFetch: PropTypes.object.isRequired,
  dispatchCreateCommunityTextPost: PropTypes.func.isRequired,
  dispatchUpdateCommunityTextPut: PropTypes.func.isRequired,
  createCommunityTextFetch: PropTypes.object.isRequired,
  updateCommunityTextFetch: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
};

export default compose(
  lookupService({ resource: 'languages', url: apiRoutes.getLanguages() }),
  communityTextsService,
  createCommunityTextService,
  updateCommunityTextService
)(CommunityTexts);
